import { useEffect, useState } from "react";
import { startOfDay, subMonths, subYears } from "date-fns";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  DateRangePicker,
  type DateRangeValue,
} from "@/components/ui/date-range-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateForAPI } from "@/lib/date-utils";
import {
  UserStatus,
  type ExportUsersRequest,
  type UserVmFilter,
} from "@/types/admin";

// ============================================================================
// Helpers
// ============================================================================

/** Quick ranges ending today. `back` walks the start date away from now. */
const PERIOD_PRESETS = [
  { key: "1m", label: "1 month", back: (now: Date) => subMonths(now, 1) },
  { key: "3m", label: "3 months", back: (now: Date) => subMonths(now, 3) },
  { key: "6m", label: "6 months", back: (now: Date) => subMonths(now, 6) },
  { key: "1y", label: "1 year", back: (now: Date) => subYears(now, 1) },
] as const;

type PresetKey = (typeof PERIOD_PRESETS)[number]["key"];

/**
 * VM choices, as the wire value each one sends.
 *
 * "All" is the absence of the field rather than a value of its own, so the Select's string keys and
 * the request's `UserVmFilter | undefined` are mapped here in one place instead of at both ends of
 * the component.
 */
const VM_FILTER_OPTIONS = [
  { key: "All", label: "All customers", value: undefined },
  { key: "HasVm", label: "Has created a VM", value: "HasVm" },
  { key: "NoVm", label: "Has not created a VM", value: "NoVm" },
  {
    key: "ExpiredNotRenewed",
    label: "VM expired, not renewed",
    value: "ExpiredNotRenewed",
  },
] as const satisfies readonly {
  key: string;
  label: string;
  value: UserVmFilter | undefined;
}[];

type VmFilterKey = (typeof VM_FILTER_OPTIONS)[number]["key"];

/** The preset applied when the dialog opens, so Export is usable without touching the picker. */
const DEFAULT_PRESET: PresetKey = "1m";

/**
 * Builds the range for a preset.
 *
 * Both ends are emitted at 00:00 local, exactly like the date picker does, so the single
 * end-of-day widening in handleExport stays the only place that logic lives.
 */
function buildPresetRange(key: PresetKey): DateRangeValue {
  const preset = PERIOD_PRESETS.find((candidate) => candidate.key === key)!;
  const today = startOfDay(new Date());

  return {
    from: startOfDay(preset.back(today)).toISOString(),
    to: today.toISOString(),
  };
}

// ============================================================================
// Types
// ============================================================================

interface ExportUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires with the payload for a filter-mode export (no rows ticked). */
  onExport: (payload: ExportUsersRequest) => void;
  isExporting?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ExportUsersDialog({
  open,
  onOpenChange,
  onExport,
  isExporting = false,
}: ExportUsersDialogProps) {
  const [status, setStatus] = useState<UserStatus | "All">("All");
  const [vmFilter, setVmFilter] = useState<VmFilterKey>("All");
  const [range, setRange] = useState<DateRangeValue>(() =>
    buildPresetRange(DEFAULT_PRESET),
  );
  const [activePreset, setActivePreset] = useState<PresetKey | null>(
    DEFAULT_PRESET,
  );

  // The dialog is mounted for the page's lifetime, so a previous run's choices would still be
  // sitting there on the next open. Reset when it opens, not when it closes, so the values do not
  // visibly clear during the close animation.
  useEffect(() => {
    if (open) {
      setStatus("All");
      setVmFilter("All");
      setRange(buildPresetRange(DEFAULT_PRESET));
      setActivePreset(DEFAULT_PRESET);
    }
  }, [open]);

  const hasFullRange = !!range.from && !!range.to;

  const applyPreset = (key: PresetKey) => {
    setRange(buildPresetRange(key));
    setActivePreset(key);
  };

  const handleRangeChange = (value: DateRangeValue) => {
    setRange(value);
    // A hand-picked range no longer corresponds to any preset, so drop the highlight rather than
    // leaving a badge looking active while the dates say something else.
    setActivePreset(null);
  };

  const handleExport = () => {
    if (!hasFullRange) {
      return;
    }

    onExport({
      userIds: [],
      status: status === "All" ? undefined : status,
      vmFilter: VM_FILTER_OPTIONS.find((option) => option.key === vmFilter)
        ?.value,
      // Sent as the picked calendar day plus this client's UTC offset (2026-08-21T00:00:00+07:00).
      // The server expands each end to the whole of that day; doing it here instead would need
      // the client and server to agree on the boundary, and `toISOString()` would strip the
      // offset the server needs to know which day was meant.
      registeredFrom: formatDateForAPI(range.from),
      registeredTo: formatDateForAPI(range.to),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // The export is a POST already on its way; closing mid-flight would leave the operator
        // unable to tell whether the file is still coming.
        if (next || !isExporting) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(event) => {
          if (isExporting) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (isExporting) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Export Users to Excel</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            No rows are selected, so the export follows these filters. The file
            contains name, phone number and email.
          </DialogDescription>
          <div className="space-y-2">
            <Label htmlFor="exportUserStatus">Customer type</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as UserStatus | "All")}
            >
              <SelectTrigger id="exportUserStatus">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All customers</SelectItem>
                <SelectItem value={UserStatus.Active}>Active</SelectItem>
                <SelectItem value={UserStatus.Suspended}>Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="exportUserVmFilter">VM usage</Label>
            <Select
              value={vmFilter}
              onValueChange={(value) => setVmFilter(value as VmFilterKey)}
            >
              <SelectTrigger id="exportUserVmFilter">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent>
                {VM_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            {/* No htmlFor: DateRangePicker takes no id, so a label pointing at one is inert. */}
            <Label>
              Registration period <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {PERIOD_PRESETS.map((preset) => (
                <Button
                  key={preset.key}
                  type="button"
                  size="sm"
                  variant={activePreset === preset.key ? "default" : "outline"}
                  onClick={() => applyPreset(preset.key)}
                  aria-pressed={activePreset === preset.key}
                  className="h-7 rounded-full px-3 text-xs hover:translate-y-0 active:translate-y-0"
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            <DateRangePicker
              value={range}
              onChange={handleRangeChange}
              placeholder="Select a period"
            />
            {!hasFullRange && (
              <p className="text-xs text-muted-foreground">
                Pick both a start and an end date — a period is required when
                exporting without a selection.
              </p>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={!hasFullRange || isExporting}
          >
            {isExporting ? (
              <Spinner className="mr-2" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
